export interface SurveyOption {
  label: string;
  subsurvey?: SubSurveyOption[];
  hideCommentField?: boolean;
  commentFieldLabel?: string;
}

export interface SubSurveyOption {
  label: string;
  hideCommentField?: boolean;
  passedAway?: boolean;
}

export const SURVEY_OPTION_LABELS = {
  DIFFERENT_ISSUE: `I have a different issue not listed above`,
  MOVING_ABROAD: `I'm moving abroad, or to Alaska or Hawaii`,
} as const;

export const SURVEY_OPTIONS: SurveyOption[] = [
  {
    label: `It's too expensive`,
    subsurvey: [
      { label: `I can't afford it within my budget` },
      { label: `It didn't meet my expectations for the price` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `My dog doesn't like it`,
    subsurvey: [
      { label: `My dog picks out or eats around certain ingredients` },
      { label: `My dog is not eating the food (e.g., sniffs and walks away) ` },
      { label: `My dog eats the food, but just doesn't love it` },
      { label: `My dog has trouble chewing the food (e.g., issues with size or texture)` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `My dog had a negative reaction (e.g., digestive, stool, vomiting, or allergy issues)`,
    subsurvey: [
      { label: `My dog is vomiting` },
      { label: `My dog is having stool or digestive issues` },
      { label: `My dog had an allergic reaction` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I haven't noticed any benefit`,
    subsurvey: [
      { label: `I expected increased energy` },
      { label: `I expected clearer skin or a shinier coat` },
      { label: `I expected better digestion and stools` },
      { label: `I expected less itching and scratching` },
      { label: `I expected my dog to lose weight` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `The recommended portions or daily calories aren't right for my dog`,
    subsurvey: [
      { label: `I think my dog needs more food per day` },
      { label: `I think my dog needs less food per day` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I don't want a subscription`,
    subsurvey: [
      { label: `I prefer to order one-off, but I'll re-order when I'm ready` },
      {
        label: `I dislike subscriptions in general, so I'll buy another product that I can purchase one-off`,
      },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `My dog is no longer with me`,
    subsurvey: [
      { label: `My dog passed away`, hideCommentField: true, passedAway: true },
      { label: `My dog no longer lives with me`, hideCommentField: true },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I'm unhappy with the quality of the food`,
    subsurvey: [
      { label: `There's not enough meat` },
      { label: `I'm concerned about ingredients in or not in the recipes` },
      { label: `It's too soft or crumbly` },
      { label: `It's too hard` },
      { label: `It has a strong or unpleasant smell` },
      { label: `It looks different than advertised` },
      { label: `The quality varies order to order` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I had issues with my deliveries`,
    subsurvey: [
      { label: `Order(s) took too long to arrive` },
      { label: `Items in my box were missing or incorrect` },
      { label: `Box(es) arrived damaged` },
      { label: `Food arrived unuseable (e.g., spoiled, opened, etc.)` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I'm running out of food or have too much food leftover between orders`,
    subsurvey: [
      { label: `I'm running out of food` },
      { label: `I have too much food` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `Feeding this food is too difficult or inconvenient`,
    subsurvey: [
      { label: `Portioning the food correctly is difficult` },
      { label: `Serving the food is messy` },
      { label: `The packaging is hard to open` },
      { label: `It's difficult to store or takes up too much space` },
      { label: `Being home soon enough after deliveries is inconvenient` },
      { label: `It's too hard to take on-the-go or travel with` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I had issues with the website`,
    subsurvey: [
      { label: `I had trouble accessing my account` },
      { label: `I found it confusing to manage my account` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I had issues with customer service`,
    subsurvey: [
      { label: `I had trouble finding a way to contact you` },
      { label: `I had trouble finding a way to speak with someone on the phone` },
      { label: `I didn't receive a response from your team` },
      { label: `The individual(s) I spoke to didn't seem knowledgeable` },
      {
        label: `The individual(s) I spoke to didn't make me feel heard (e.g., rude, unwilling to help, etc.)`,
      },
      { label: `I didn't agree with a company policy that was explained to me` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: `I need food specific to my dog's medical issues or lifestage`,
    subsurvey: [
      { label: `Weight Loss / Management` },
      { label: `Urinary Support` },
      { label: `Thyroid Support` },
      { label: `Skin Support` },
      { label: `Liver Support` },
      { label: `Kidney Support` },
      { label: `Joint Support` },
      { label: `Allergy Support` },
      { label: `Digestive Support` },
      { label: `Diabetes Support` },
      { label: `Pancreatitis Support / Low Fat` },
      { label: `Gestation/Lactation Lifestage (pregnancy and nursing)` },
      { label: `Growth Lifestage (puppies)` },
      { label: `Maintenance Lifestage (adults)` },
      { label: `Other` },
      { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
    ],
  },
  {
    label: SURVEY_OPTION_LABELS.MOVING_ABROAD,
    commentFieldLabel: `What country or state are you moving to? We hope to expand to your new location in the future`,
  },
  { label: SURVEY_OPTION_LABELS.DIFFERENT_ISSUE },
  { label: `No reason provided (CX USE ONLY)`, hideCommentField: true },
];
